import { inject, Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger/logger-service/logger.service';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import {
    AccountInfo,
    AuthenticationResult,
    EventMessage,
    EventType,
    IdTokenClaims,
    InteractionStatus,
    PopupRequest,
    PromptValue,
    RedirectRequest,
} from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { TokenService } from '../token-service/token.service';
import { AuthService } from '../auth-service.interface';
import { AuditLogsService } from '../../services/audit-logs-service/audit-logs.service';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
    acr?: string;
    tfp?: string;
};

@Injectable({
    providedIn: 'root',
})
export class AppMsalAuthService implements AuthService {
    private readonly _destroying$ = new Subject<void>();

    // Injectables
    private logger = inject(LoggerService);
    private msalService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);
    private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
    private router = inject(Router);
    private tokenService = inject(TokenService);
    private auditLogsService = inject(AuditLogsService);

    constructor() {
        this.setupSubscriptions();
    }

    login() {
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    }

    logout() {
        this.auditLogsService.logLogout();
        this.msalService.logoutRedirect();
    }

    isLoggedIn(): boolean {
        return this.msalService.instance.getAllAccounts().length > 0; // this.user() !== null;
    }

    private async setTokenFromActiveAccount() {
        if (this.tokenService.getToken()) {
            return;
        }
        const account = this.msalService.instance.getActiveAccount();
        if (account === null) {
            return;
        }
        const token = await this.msalService.instance.acquireTokenSilent({
            scopes: environment.apiConfig.scopes,
            account,
        });
        this.tokenService.setToken(token.accessToken);
    }

    private setupSubscriptions(): void {
        this.msalService.handleRedirectObservable().subscribe(); // Subscribing to handleRedirectObservable before any other functions both initializes the application and ensures redirects are handled

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$),
            )
            .subscribe(() => {
                this.checkAndSetActiveAccount();
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_SUCCESS ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                        msg.eventType === EventType.SSO_SILENT_SUCCESS,
                ),
                takeUntil(this._destroying$),
            )
            .subscribe((result: EventMessage) => {
                this.checkAndSetActiveAccount();
                const payload = result.payload as AuthenticationResult;
                const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

                if (
                    idtoken.acr === environment.b2cPolicies.names.signUpSignIn ||
                    idtoken.tfp === environment.b2cPolicies.names.signUpSignIn
                ) {
                    this.msalService.instance.setActiveAccount(payload.account);
                }

                /**
                 * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
                 * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
                 * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
                 */
                if (
                    idtoken.acr === environment.b2cPolicies.names.editProfile ||
                    idtoken.tfp === environment.b2cPolicies.names.editProfile
                ) {
                    // retrieve the account from initial sing-in to the app
                    const originalSignInAccount = this.msalService.instance
                        .getAllAccounts()
                        .find(
                            (account: AccountInfo) =>
                                account.idTokenClaims?.oid === idtoken.oid &&
                                account.idTokenClaims?.sub === idtoken.sub &&
                                ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr ===
                                    environment.b2cPolicies.names.signUpSignIn ||
                                    (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp ===
                                        environment.b2cPolicies.names.signUpSignIn),
                        );

                    const signUpSignInFlowRequest: PopupRequest = {
                        scopes: [...environment.apiConfig.scopes],
                        authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                        account: originalSignInAccount,
                        prompt: PromptValue.NONE,
                    };

                    // To get the updated account information
                    this.msalService.acquireTokenPopup(signUpSignInFlowRequest);
                }

                /**
                 * Below we are checking if the user is returning from the reset password flow.
                 * If so, we will ask the user to reauthenticate with their new password.
                 * If you do not want this behavior and prefer your users to stay signed in instead,
                 * you can replace the code below with the same pattern used for handling the return from
                 * profile edit flow (see above ln. 74-92).
                 */
                if (
                    idtoken.acr === environment.b2cPolicies.names.resetPassword ||
                    idtoken.tfp === environment.b2cPolicies.names.resetPassword
                ) {
                    const signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
                        authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                        scopes: [...environment.apiConfig.scopes],
                        prompt: PromptValue.LOGIN, // force user to reauthenticate with their new password
                    };

                    // this.login(signUpSignInFlowRequest);
                }

                this.setTokenFromActiveAccount();
                if (result.eventType === EventType.LOGIN_SUCCESS) {
                    this.router.navigate([this.router.url]); // Manual refresh to ensure page properly loads
                }
                return result;
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE,
                ),
                takeUntil(this._destroying$),
            )
            .subscribe((result: EventMessage) => {
                // Check for forgot password error
                // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
                if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
                    const resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
                        authority: environment.b2cPolicies.authorities.resetPassword.authority,
                        scopes: [],
                    };

                    // this.login(resetPasswordFlowRequest);
                }
            });
    }

    private checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        const activeAccount = this.msalService.instance.getActiveAccount();

        if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
            const accounts = this.msalService.instance.getAllAccounts();
            this.msalService.instance.setActiveAccount(accounts[0]);
        }

        this.setTokenFromActiveAccount();
    }
}
